import {
  AritoIcon,
  BulkDeleteDialogV4,
  Button,
  ErpDataTableV2,
  ExcelImportDialog,
  ExcelImportErrorDialog
} from '@/components/custom/erp';
import { AccountFormProvider, AccountPageProvider, useAccountPageContext } from './context';
import { ActionBar, ConfirmDialog, FormDialog } from './components';
import { ACCOUNT_TABLE_COLUMNS } from './cols-definition';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ACCOUNT_GROUPS } from './constants';
import { cn } from '@/lib/utils';

function AccountGroupPageContent() {
  const page = useAccountPageContext();

  return (
    <div className='flex h-screen w-full overflow-hidden bg-white'>
      <aside className='w-[280px] shrink-0 border-r border-slate-200 bg-white'>
        <ScrollArea className='h-full'>
          <div className='p-4'>
            <h2 className='mb-4 flex items-center gap-2 text-lg font-semibold'>
              <AritoIcon icon={539} /> Nhóm tài khoản
            </h2>
            <Separator className='mb-4 bg-slate-200' />

            <nav aria-label='Nhóm tài khoản'>
              <ul className='space-y-2'>
                {ACCOUNT_GROUPS.map(group => {
                  const isActive = page.activeGroup === group.value;

                  return (
                    <li key={group.value} className='text-sm'>
                      <Button
                        type='button'
                        variant='ghost'
                        onClick={() => page.handleGroupChange(group.value)}
                        className={cn(
                          'h-auto justify-start rounded-lg px-2 py-1.5 text-left text-sm font-normal text-slate-700',
                          'hover:bg-slate-100 hover:text-slate-900',
                          isActive
                            ? 'mx-4 w-[calc(100%-32px)] bg-gray-200 text-blue-500 hover:bg-gray-200 hover:text-blue-500'
                            : 'mx-2 w-[calc(100%-16px)]'
                        )}
                      >
                        {group.label}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </ScrollArea>
      </aside>

      <section className='flex min-w-0 flex-1 flex-col overflow-hidden'>
        <ActionBar
          onAddClick={page.handleOpenAdd}
          onEditClick={page.handleOpenEdit}
          onDeleteClick={page.handleRequestDelete}
          onCopyClick={page.handleOpenCopy}
          onRefreshClick={page.handleRefresh}
          onViewClick={page.handleOpenView}
          onImportExcel={page.handleOpenImportExcel}
          isRowActionDisabled={page.isRowActionDisabled}
          isDeleteDisabled={page.isDeleteActionDisabled}
        />

        <div className='min-h-0 flex-1 overflow-hidden'>
          <ErpDataTableV2
            tables={[
              {
                name: 'Tài khoản',
                rows: page.paginatedRows,
                columns: ACCOUNT_TABLE_COLUMNS
              }
            ]}
            totalItems={page.totalItems}
            currentPage={page.currentPage}
            pageSize={page.pageSize}
            onPageChange={page.handlePageChange}
            selectedRowId={page.selectedRowId || undefined}
            getRowId={row => row.uuid}
            onRowClick={page.handleRowClick}
            loading={page.isLoading}
            widthMode='stretch'
            className='h-full'
          />
        </div>
      </section>

      {page.isBulkDeleteOpen && (
        <BulkDeleteDialogV4
          title='Tài khoản'
          onClose={page.handleCloseBulkDelete}
          columns={ACCOUNT_TABLE_COLUMNS}
          dataRows={page.paginatedRows}
          handleDelete={page.handleDeleteByUuid}
          onComplete={() => void page.handleRefresh()}
        />
      )}

      {page.isDeleteOpen && page.selectedRow && (
        <ConfirmDialog
          open={page.isDeleteOpen}
          onClose={page.handleCloseDelete}
          onConfirm={page.handleConfirmDelete}
          title='Xóa tài khoản'
          content={`Bạn có chắc chắn muốn xóa tài khoản "${page.selectedRow.code} - ${page.selectedRow.name}" không?`}
          isLoading={page.isDeleting}
        />
      )}

      {page.isFormOpen && (
        <AccountFormProvider
          account={page.formAccount}
          mode={page.formMode}
          isCopyMode={page.isCopyMode}
          onClose={page.handleCloseForm}
          onSuccess={page.handleFormSuccess}
        >
          <FormDialog />
        </AccountFormProvider>
      )}

      {page.isImportDialogOpen && (
        <ExcelImportDialog
          open={page.isImportDialogOpen}
          onClose={() => page.setIsImportDialogOpen(false)}
          onSubmit={page.handleImportFromExcel}
          title='Lấy dữ liệu từ Excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {page.showErrorDialog && page.errorData && (
        <ExcelImportErrorDialog
          open={page.showErrorDialog}
          onClose={() => {
            page.setShowErrorDialog(false);
          }}
          errors={page.errorData.errors}
          columnMapping={page.errorData.columnMapping}
        />
      )}
    </div>
  );
}

export default function AccountGroupPage() {
  return (
    <AccountPageProvider>
      <AccountGroupPageContent />
    </AccountPageProvider>
  );
}
